import { useState, useEffect } from 'react';
import { Settings, Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { adminAPI } from '@/lib/api';

export function SystemSettingsTab() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');

  // Editable state
  const [pricingFree, setPricingFree] = useState({ price: 0, scans_limit: 5 });
  const [pricingRegular, setPricingRegular] = useState({ price: 49, scans_limit: 50 });
  const [pricingProcurement, setPricingProcurement] = useState({ price: 99, scans_limit: 200 });
  const [regDomains, setRegDomains] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await adminAPI.listSettings();
        const map = {};
        (res.data.settings || []).forEach(s => { map[s.key] = s.value; });
        setSettings(map);

        if (map.pricing_tiers) {
          setPricingFree(map.pricing_tiers.free || { price: 0, scans_limit: 1 });
          setPricingRegular(map.pricing_tiers.compliance_regular || { price: 49, scans_limit: -1 });
          setPricingProcurement(map.pricing_tiers.procurement || { price: 199, scans_limit: 500 });
        }
        if (map.allowed_regulatory_domains) {
          setRegDomains((map.allowed_regulatory_domains || []).join(', '));
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetch();
  }, []);

  const savePricing = async () => {
    setSaving('pricing');
    try {
      await adminAPI.updateSetting('pricing_tiers', { free: pricingFree, compliance_regular: pricingRegular, procurement: pricingProcurement });
      toast.success('Pricing tiers updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving('');
  };

  const saveDomains = async () => {
    setSaving('domains');
    try {
      const domains = regDomains.split(',').map(d => d.trim()).filter(Boolean);
      await adminAPI.updateSetting('allowed_regulatory_domains', domains);
      toast.success('Regulatory domains updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving('');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-system-settings">
      {/* Pricing Tiers */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Monthly Subscription Pricing Tiers</CardTitle>
          <CardDescription>Manage monthly pricing and scan limits for each plan.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[{ label: 'Free', state: pricingFree, setter: setPricingFree }, { label: 'Compliance Regular', state: pricingRegular, setter: setPricingRegular }, { label: 'Procurement', state: pricingProcurement, setter: setPricingProcurement }].map(({ label, state, setter }) => (
            <div key={label} className="flex items-center gap-4 rounded-lg border border-border/60 px-4 py-3">
              <Badge variant="secondary" className="text-xs w-[140px] justify-center">{label}</Badge>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground w-20">Monthly Price $</Label>
                <Input type="number" value={state.price} onChange={e => setter({...state, price: Number(e.target.value)})} className="w-24 h-8 bg-muted/30 border-border text-sm" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground w-28">Monthly Scan Limit</Label>
                <Input type="number" value={state.scans_limit} onChange={e => setter({...state, scans_limit: Number(e.target.value)})} className="w-24 h-8 bg-muted/30 border-border text-sm" />
              </div>
            </div>
          ))}
          <Button onClick={savePricing} disabled={saving === 'pricing'} data-testid="save-pricing-button">
            {saving === 'pricing' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save Pricing
          </Button>
        </CardContent>
      </Card>

      <Separator className="opacity-50" />

      {/* Regulatory Domains */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Allowed Regulatory Email Domains</CardTitle>
          <CardDescription>Users with emails from these domains can select the Regulatory role.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Domains (comma-separated)</Label>
            <Input value={regDomains} onChange={e => setRegDomains(e.target.value)} placeholder="justice.gov, pr.gov, ..." className="bg-muted/30 border-border" data-testid="regulatory-domains-input" />
          </div>
          <Button onClick={saveDomains} disabled={saving === 'domains'} data-testid="save-domains-button">
            {saving === 'domains' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save Domains
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
